import React, { ReactNode } from 'react'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Icon, { IconName } from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import SectionHeader from 'components_new/molecules/SectionHeader'

export interface DataHealthSectionCardProps {
  /** Buttons at the right end of the header, after the meta. */
  actions?: ReactNode
  children: ReactNode
  /** Rendered below the children, above the card's bottom edge. */
  footer?: ReactNode
  /**
   * A small square in a status colour, e.g. `error.main` for a card about
   * failures. Mutually exclusive with `icon`; when neither is set the header
   * has no leading mark.
   */
  square?: string
  icon?: IconName
  /** Right-aligned, dimmed line explaining the card's sort order or scope. */
  meta?: ReactNode
  /** Count pill next to the title. Omit to render no pill. */
  pill?: { color?: 'error' | 'warning' | 'default'; label: string }
  title: string
}

/**
 * The Datahälsa card shell: a white surface with a 1px border, a header
 * carrying a status mark, a title, an optional count pill and right-aligned
 * meta, and an optional footer.
 *
 * The same shell is inlined in the seven Datakörningar organisms. It is not
 * retrofitted onto those here - that page's body is meant to move into its
 * tab untouched - so for now this is the shell for new cards only.
 */
const DataHealthSectionCard = (props: DataHealthSectionCardProps) => {
  const { actions, children, footer, icon, meta, pill, square, title } = props

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '20px',
        p: '20px 22px',

        // The column widths below are narrower than some two-word headers
        // plus the cell's own padding, which would otherwise wrap.
        '& thead th': { whiteSpace: 'nowrap' }
      }}
    >
      <SectionHeader
        actions={
          meta || actions ? (
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
              {meta ? (
                <Text sx={{ color: 'text.disabled' }} variant="caption">
                  {meta}
                </Text>
              ) : null}
              {actions}
            </Box>
          ) : null
        }
        icon={
          square ? (
            <Box
              sx={{
                bgcolor: square,
                borderRadius: '2px',
                height: '10px',
                width: '10px'
              }}
            />
          ) : icon ? (
            <Icon
              fontSize="small"
              name={icon}
              sx={{ color: 'text.disabled' }}
            />
          ) : undefined
        }
        title={title}
        titleAdornment={
          pill ? (
            <Chip
              color={pill.color ?? 'default'}
              label={pill.label}
              size="small"
            />
          ) : null
        }
      />

      {children}

      {footer ? (
        <Box
          sx={{
            alignItems: 'center',
            borderTop: '1px solid',
            borderTopColor: 'divider',
            display: 'flex',
            gap: 1,
            mt: 1,
            pt: 1.5
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Box>
  )
}

DataHealthSectionCard.displayName = 'DataHealthSectionCard'
export default DataHealthSectionCard
